import { Component, Input, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { ProfileService } from "src/app/api/profile.service";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { AuthService } from "src/app/api/auth.service";
import { GoogleAnalyticsService, UserEvent } from "src/app/services/google-analytics.service";
import { HCaptchaService } from "src/app/services/hcaptcha.service";
import { catchError, of, switchMap } from "rxjs";
import { CustomValidators } from "src/app/validators/custom-validators";

@Component({
    selector: "auth-register",
    templateUrl: "./register.component.html",
    styleUrls: ["./register.component.scss"],
    standalone: false
})
export class RegisterComponent implements OnInit {
  @Input() referralId: string;

  loading = false;
  loadingCaptcha = false;
  error: string = null;

  form = this.fb.group({
    firstName: ["", Validators.required],
    lastName: ["", Validators.required],
    email: ["", [Validators.required, CustomValidators.emailValidator]],
    password: [""],
  });

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private profileService: ProfileService,
    private hCaptchaService: HCaptchaService,
    private ga: GoogleAnalyticsService
  ) { }

  ngOnInit() {
    this.checkQueryParams();
  }

  checkQueryParams() {
    // email
    const email = this.route.snapshot.queryParamMap.get("email");
    if (email) {
      this.email.setValue(email);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.error = "Please fill out all the required fields";
      return;
    }

    this.error = null;
    this.loading = true;
    let captcha = "";

    if (this.hCaptchaService.isCaptchaEnabled) {
      this.loadingCaptcha = true;
      try {
        captcha = await this.hCaptchaService.getCaptcha();
      } catch (err) {
        console.error("hcaptcha_ERROR", err);
        this.loadingCaptcha = false;
        this.loading = false;
        return;
      } finally {
        this.loadingCaptcha = false;
      }
    }

    const { firstName, lastName, email, password } = this.form.value;

    this.profileService
      .registerUser(
        { firstName, lastName, email, password },
        this.referralId,
        captcha
      )
      .pipe(
        switchMap(() =>
          this.hCaptchaService.getPassiveCaptcha()
        ),
        switchMap((passiveCaptcha) =>
          this.auth.setCredentials(email, password).login(passiveCaptcha)
        ),
        catchError((err) => {
          this.error =
            err?.errors?.[0]?.message ||
            err?.error?.error_description ||
            "Registration failed";
          this.loading = false;
          return of(null);
        })
      )
      .subscribe((loginResponse) => {
        if (!loginResponse) return;
        this.ga.sendEvent("Account", {
          action: "Sign Up",
          label: "Registered",
        });
        this.ga.trackUsersAuthEvents(UserEvent.sign_up, "email", this.auth.getToken().userId, email);
        this.router.navigate(["/settings"]);
        this.loading = false;
      });
  }

  get firstName() {
    return this.form.get("firstName");
  }
  get lastName() {
    return this.form.get("lastName");
  }
  get email() {
    return this.form.get("email");
  }
  get password() {
    return this.form.get("password");
  }
}
